import { Box, Card, CardContent, Skeleton, Stack, Typography, useTheme } from '@mui/joy';
import { ChartOptions } from 'chart.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LabeledValue } from 'core/elements';
import { getAbbreviatedNumber } from 'core/utils/number';
import { PricingHelper } from 'pricing/helpers';
import { PieChart } from 'reports/components/charts';
import { IQuotesDashboardSummary } from 'reports/types';
import { Direction, Level, SkeletonVariant, Variant } from 'theme/enums';

import { QuotesDashboardQuoteType } from './enums';

const CARD_HEIGHT = 320;
const CHART_HEIGHT = 200;

const CHART_COLORS = {
  orders: '#125DCB',
  quotes: '#6FAFFF',
};

interface IProps {
  data?: IQuotesDashboardSummary;
  isLoading: boolean;
  type: QuotesDashboardQuoteType;
}

export const VolumeComparisonCard = ({ data, isLoading, type }: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const isCustomer = type === QuotesDashboardQuoteType.CUSTOMER;

  const ordersData = isCustomer ? data?.customerOrders : data?.vendorOrders;
  const quotesData = isCustomer ? data?.customerQuotes : data?.vendorQuotes;

  const ordersCount = ordersData?.count ?? 0;
  const quotesCount = quotesData?.count ?? 0;

  const ordersValue = isCustomer ? ordersData?.totalPrice ?? 0 : ordersData?.totalCost ?? 0;
  const quotesValue = isCustomer ? quotesData?.totalPrice ?? 0 : quotesData?.totalCost ?? 0;

  const totalQuantity = ordersCount + quotesCount;
  const totalValue = ordersValue + quotesValue;

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          backgroundColor: [CHART_COLORS.orders, CHART_COLORS.quotes],
          borderWidth: 0,
          data: [ordersCount, quotesCount],
        },
      ],
      labels: [t('labelOrders'), t('labelQuotes')],
    }),
    [ordersCount, quotesCount, t]
  );

  const chartOptions: ChartOptions<'pie'> = useMemo(
    () => ({
      plugins: {
        datalabels: {
          color: theme.palette.common.white,
          font: {
            size: 14,
            weight: 'bold',
          },
          formatter: (_value: number, context: { dataIndex: number }) => {
            const isOrders = context.dataIndex === 0;
            const count = isOrders ? ordersCount : quotesCount;
            const value = isOrders ? ordersValue : quotesValue;
            const formattedCount = getAbbreviatedNumber(count);
            const formattedValue = PricingHelper.formatPricing({ amount: value });

            return `${formattedCount}\n${formattedValue}`;
          },
          textAlign: 'center',
        },
        legend: {
          labels: {
            boxWidth: 16,
            font: {
              size: 16,
            },
            padding: 8,
          },
          position: 'right',
        },
      },
    }),
    [ordersCount, ordersValue, quotesCount, quotesValue, theme.palette.common.white]
  );

  const formattedTotalQuantity = getAbbreviatedNumber(totalQuantity);
  const formattedTotalValue = PricingHelper.formatPricing({ amount: totalValue });

  if (isLoading) {
    return (
      <Card variant={Variant.OUTLINED} sx={{ height: CARD_HEIGHT }}>
        <CardContent>
          <Skeleton sx={{ height: 24, width: '50%' }} variant={SkeletonVariant.TEXT} />
          <Skeleton sx={{ height: 200, width: '100%' }} variant={SkeletonVariant.RECTANGULAR} />
          <Stack direction={Direction.ROW} sx={{ gap: 4, mt: 2 }}>
            <Skeleton sx={{ height: 20, width: '40%' }} variant={SkeletonVariant.TEXT} />
            <Skeleton sx={{ height: 20, width: '40%' }} variant={SkeletonVariant.TEXT} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant={Variant.OUTLINED} sx={{ height: CARD_HEIGHT }}>
      <CardContent>
        <Typography level={Level.TITLE_MD} sx={{ mb: 2, mt: 1 }}>
          {t('labelCompareVolumes')}
        </Typography>
        <Box sx={{ height: CHART_HEIGHT }}>
          <PieChart data={chartData} height="100%" options={chartOptions} />
        </Box>
        <Stack direction={Direction.ROW} sx={{ gap: 4, mt: 2 }}>
          <LabeledValue
            label={t('labelTotalQuantity')}
            options={{
              inline: true,
            }}
            value={formattedTotalQuantity}
          />
          <LabeledValue
            label={t(isCustomer ? 'labelTotalPrice' : 'labelTotalCost')}
            options={{
              inline: true,
            }}
            value={formattedTotalValue}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};
